import { ArrowLeft, Columns2, Moon, Sun } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, type RouteObject } from 'react-router-dom';
import { MainMenu } from '@/components/layout/shell/MainMenu';
import { WorkspaceCanvas } from '@/components/layout/workspace/WorkspaceCanvas';
import { WorkspaceProvider } from '@/components/layout/workspace/WorkspaceProvider';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import type { RightPanelConfig } from '@/components/layout/workspace/rightPanelContext';
import { ObserverProvider } from '@/components/observer/ObserverProvider';
import { Badge, Button, IconButton, ToastProvider } from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { HOME, RECORDS, panelOf, resolveSample } from './model';
import { RecordList } from './RecordList';
import { RecordDetails } from './RecordDetails';
import { RecordPage, PersonPage } from './RecordPage';
import { RecordsProvider } from './RecordsProvider';
import { useRecords } from './recordsContext';

const routes: RouteObject[] = [
  { path: '/record/:id', element: <RecordPage /> },
  { path: '/person/:id', element: <PersonPage /> },
  { path: '*', element: <RecordList /> },
];

export function WorkspacePatterns() {
  const [embedded] = useState(() => new URLSearchParams(window.location.search).has('embedded'));
  return (
    <ToastProvider>
      <RecordsProvider>
        <WorkspaceProvider homePath={HOME} resolve={resolveSample} storageKey="kaname.patterns.workspace.v1">
          <ObserverProvider home="core" storageKey="kaname.patterns.observer.v1">
            <SampleShell embedded={embedded} />
          </ObserverProvider>
        </WorkspaceProvider>
      </RecordsProvider>
    </ToastProvider>
  );
}

function SampleShell({ embedded }: { embedded: boolean }) {
  const ws = useWorkspace();
  const { records } = useRecords();
  const { mode, toggle } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const panelConfig = useMemo<RightPanelConfig>(
    () => ({
      storageKey: 'kaname.patterns.panels.v1',
      render: (item) => <RecordDetails item={item} />,
      title: (item) => records.find((r) => r.id === item.id)?.title ?? item.title,
    }),
    [records],
  );
  return (
    <div className="flex h-screen flex-col bg-frame text-fg">
      {!embedded && (
        <header className="flex shrink-0 flex-wrap items-center gap-4 border-b border-line bg-canvas px-6 py-3">
          <Link
            to="/design#workspace-patterns"
            className="inline-flex items-center gap-2 text-xs font-medium text-fg-muted hover:text-brand-fg"
          >
            <ArrowLeft className="size-4" />
            Галерея
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="type-subheading">Элементы рабочего пространства</h1>
            <p className="text-xs text-fg-muted">
              Панель · свойства · длинные списки · деньги и сроки · статусы
            </p>
          </div>
          <Badge>Образец</Badge>
          <Button variant="secondary" size="sm" onClick={() => ws.openBeside(panelOf(RECORDS[1]).path)}>
            <Columns2 className="size-4" />
            Открыть второе окно
          </Button>
          <IconButton
            variant="secondary"
            aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            onClick={toggle}
          >
            {mode === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </header>
      )}
      <div data-shell="" className="relative flex min-h-0 flex-1 gap-3 p-3">
        <MainMenu
          expanded={expanded}
          onToggleExpand={() => setExpanded((v) => !v)}
          homeActive
          onHome={() => ws.go(HOME)}
          activeId={null}
          sections={[]}
          tools={[]}
          onSelect={() => ws.go(HOME)}
        />
        <WorkspaceCanvas routes={routes} rightPanel={panelConfig} />
      </div>
    </div>
  );
}
